import React, { useState, useContext, useEffect } from "react";
import uuid from "uuid";
import _ from "lodash";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Card } from "@material-ui/core";

import { Classes } from "jss";
import { Theme } from "@material-ui/core/styles/createMuiTheme";

import { PageRoot, PageHeader, Button } from "../../components";
import routes from "../../constants/routes";
import {
  MAX_FILE_SIZE,
  GENERAL_FILE_TYPES,
  COMPLETED_FORM_TYPES,
  GENERAL_FILE_HINT,
  COMPLETED_FORM_HINT
} from "../../constants/files";
import { INFORMATION_ACCURACY_CONFIRMATION } from "../../constants/messages";

import { RegulatoryInstrument } from "../../constants/interfaces";
import { regulatoryInstrumentActions } from "../../reducers/regulatoryInstrument";
import { UIMessageContext } from "../../components/UIMessage";
import { RxForm } from "../../components/Form";
import FormTextField from "../../components/Fields/FormTextField";
import FileUploadField from "../../components/Fields/FileUploadField";
import { SubmitButton } from "../../components/Form/SubmitButton";
import { ResetButton } from "../../components/Form/ResetButton";
import { OnUnSaveChange } from "../../components/Form/OnUnSaveChange";
import Step from "./Step";
import api from "../../utils/api";
import downloadFile from "../../utils/downloadFile";

const useStyles = makeStyles((theme: Theme) => ({
  dropzone: {
    marginBottom: theme.spacing(2)
  },
  stepWrapper: {
    display: "flex",
    flexFlow: "row wrap"
  },
  typeahead: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(4),
    minWidth: "300px"
  },
  formCard: {
    padding: theme.spacing(4)
  },
  form: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    flexDirection: "column"
  },
  title: {
    marginBottom: theme.spacing(4)
  },
  metaContainer: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(4)
  },
  metaRow: {
    display: "flex",
    flexFlow: "row wrap",
    marginBottom: theme.spacing(2)
  },
  hintText: {
    marginBottom: theme.spacing(2)
  },
  hintWrapper: {
    marginBottom: theme.spacing(4)
  },
  buttonWrapper: {
    display: "flex",
    marginTop: theme.spacing(1),
    "&>button": {
      marginRight: "1.2em"
    }
  },
  downloadButton: {
    marginRight: theme.spacing(2)
  }
}));

type Props = RouteComponentProps & {
  classes: Classes;
  error?: Error;
  instrumentTypes: any;
  loading?: boolean;
  getInstrument: (id: string) => Promise<void>;
  setError: (error: Error) => void;
  renewInstrument: (instrument: any, callback: () => void) => Promise<void>;
  instrument?: RegulatoryInstrument;
};

const RenewRI: React.FC<Props> = props => {
  const classes = useStyles();

  const [tempCaseId] = useState(uuid());
  const [supportDocUUID] = useState(uuid());
  const { showMessage } = useContext(UIMessageContext);

  useEffect(() => {
    const {
      params: { id }
    }: any = props.match;
    props.getInstrument(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!props.instrument) return null;

  const { instrument, instrumentTypes } = props;

  if (!instrument) return null;

  return (
    <PageRoot>
      <PageHeader
        breadcrumbLinks={[
          {
            label: routes.REGULATORY_INSTRUMENTS.label,
            route: `${routes.REGULATORY_INSTRUMENTS.url}?tab=renew`
          },
          {
            label: "Renew"
          }
        ]}
        label={routes.RENEW_RI.label}
      />

      <Card className={classes.formCard}>
        <RxForm
          className={classes.form}
          confirmMessage={INFORMATION_ACCURACY_CONFIRMATION}
          onSubmit={async (value, meta, { resetForm }) => {
            const { supportingFiles, applicantReference } = value;
            if (!props.instrument) return;
            const updatedInstrument = {
              existingCaseId: props.instrument.cmAcmCaseIdentifier,
              cmAcmCaseIdentifier: tempCaseId,
              riStatus: props.instrument.riStatus,
              supportingDocsStagingId: supportingFiles.length
                ? supportDocUUID
                : undefined,
              applicantReference: applicantReference
            };

            await props.renewInstrument(updatedInstrument, () => {
              resetForm();
              props.history.push(
                `${routes.REGULATORY_INSTRUMENTS.url}?tab=inProgress`
              );
              showMessage(
                "Your regulatory instrument was successfully submitted",
                "success"
              );
            });
          }}
        >
          <OnUnSaveChange />

          <Typography
            className={classes.title}
            color="textSecondary"
            component="h2"
            variant="h5"
          >
            {routes.RENEW_RI.label}
          </Typography>

          {props.instrument && (
            <div className={classes.metaRow}>
              <div className={classes.metaContainer}>
                <Typography variant="caption" color="textSecondary">
                  Legal Entity
                </Typography>
                <Typography>{props.instrument.legalEntity}</Typography>
              </div>
              <div className={classes.metaContainer}>
                <Typography variant="caption" color="textSecondary">
                  Premises
                </Typography>
                <Typography>{props.instrument.premises}</Typography>
              </div>
              <div className={classes.metaContainer}>
                <Typography variant="caption" color="textSecondary">
                  Regulatory Instrument Type
                </Typography>
                <Typography>
                  {instrumentTypes[props.instrument.riType]}
                </Typography>
              </div>
            </div>
          )}

          <div className={classes.hintWrapper}>
            <Typography className={classes.hintText} color="textSecondary">
              Download and complete your renewal application form before
              proceeding with this process.
            </Typography>

            <div>
              <Button
                className={classes.downloadButton}
                color="primary"
                id="download-1"
                onClick={() => {
                  const caseId = (props.instrument as RegulatoryInstrument)
                    .cmAcmCaseIdentifier;
                  const token = window.localStorage.getItem("token");
                  const url = `/mcols/api/ri/downloadFile/RenewalLetter?caseId=${caseId}&token=${token}`;
                  downloadFile({ url });
                }}
              >
                Renewal Letter
              </Button>
              <Button
                className={classes.downloadButton}
                color="primary"
                id="download-2"
                onClick={() => {
                  const caseId = (props.instrument as RegulatoryInstrument)
                    .cmAcmCaseIdentifier;
                  const token = window.localStorage.getItem("token");
                  const url = `/mcols/api/ri/downloadFile/ApplicationForm?caseId=${caseId}&token=${token}`;
                  downloadFile({ url });
                }}
              >
                Application Form
              </Button>

              <Button
                className={classes.downloadButton}
                disabled={props.instrument.hasInvoice === "No"}
                color="primary"
                id="download-3"
                onClick={() => {
                  const caseId = (props.instrument as RegulatoryInstrument)
                    .cmAcmCaseIdentifier;
                  const token = window.localStorage.getItem("token");
                  const url = `/mcols/api/ri/downloadFile/Invoice?caseId=${caseId}&token=${token}`;
                  downloadFile({ url });
                }}
              >
                Invoice
              </Button>
            </div>
          </div>

          <Step
            index={1}
            instruction="Upload your completed application form *"
          >
            <FileUploadField
              required
              allowMultipleFiles={false}
              handleError={props.setError}
              onFileUpload={(files: File[]) => {
                const formData: any = new FormData();
                formData.append("uploadfile", files[0]);
                return api.post(`/ri/uploadFile/${tempCaseId}`, formData);
              }}
              onFileRemove={(file: File) =>
                api.put(`/ri/removeFile/${tempCaseId}`, file.name, {
                  headers: {
                    "Content-Type": "application/json"
                  }
                })
              }
              acceptedFileTypes={COMPLETED_FORM_TYPES}
              className={classes.dropzone}
              hint={COMPLETED_FORM_HINT}
              name="competedForm"
              id="competedForm"
              maxFileCount={1}
              maxTotalByteSize={MAX_FILE_SIZE}
            />
          </Step>

          <Step index={2} instruction="Attach any supporting documents">
            <FileUploadField
              handleError={props.setError}
              onFileUpload={(files: File[]) =>
                Promise.all(
                  files.map(file => {
                    const formData: any = new FormData();
                    formData.append("uploadfile", file);
                    return api.post(
                      `/ri/uploadFile/${supportDocUUID}`,
                      formData
                    );
                  })
                )
              }
              onFileRemove={(file: File) =>
                api.put(`/ri/removeFile/${supportDocUUID}`, file.name, {
                  headers: {
                    "Content-Type": "application/json"
                  }
                })
              }
              acceptedFileTypes={GENERAL_FILE_TYPES}
              className={classes.dropzone}
              hint={GENERAL_FILE_HINT}
              name="supportingFiles"
              id="supportingFiles"
              maxFileCount={10}
              maxTotalByteSize={MAX_FILE_SIZE}
            />
          </Step>

          <Step index={3} instruction="Add your applicant reference">
            <div className={classes.stepWrapper}>
              <FormTextField
                  id="applicantReference"
                  name="applicantReference"
                  label="Applicant Reference"
                  inputProps={{ maxLength: 64 }}
                  className={classes.typeahead}
                  defaultValue={_.get(props.instrument, "applicantReference")}
              />
            </div>
          </Step>

          <div className={classes.buttonWrapper}>
            <SubmitButton label="Submit" />
            <ResetButton
              label="Reset"
              onReset={() => {
                api.put(`/ri/removeFiles/${tempCaseId}`);
                api.put(`/ri/removeFiles/${supportDocUUID}`);
              }}
            />
            <Button
              onClick={() => {
                props.history.push(`${routes.REGULATORY_INSTRUMENTS.url}`);
              }}
              id="close"
            >
              Close
            </Button>
          </div>
        </RxForm>
      </Card>
    </PageRoot>
  );
};

const mapDispatchToProps = {
  getInstrument: regulatoryInstrumentActions.getRegulatoryInstrument,
  renewInstrument: regulatoryInstrumentActions.renewRegulatoryInstrument
};

const mapStateToProps = ({ regulatoryInstrument }: any, ownProps: Props) => {
  const {
    params: { id }
  }: any = ownProps.match;
  const instrument = regulatoryInstrument.instruments.find(
    (instrument: RegulatoryInstrument) => instrument.cmAcmCaseIdentifier === id
  );
  return {
    instrument,
    instrumentTypes: regulatoryInstrument.instrumentTypes
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RenewRI);
